import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Dashboard from "@/pages/Dashboard";
import PortfolioMatrix from "@/pages/PortfolioMatrix";
import CompanyNews from "@/pages/CompanyNews";
import LoginPage from "@/pages/LoginPage";
import StoryDeepDive from "@/pages/StoryDeepDive";
import PortfolioDataSettings from "@/pages/settings/PortfolioDataSettings";
import FlaggedCompanies from "@/pages/FlaggedCompanies";
import WatchlistNewsFeed from "@/pages/WatchlistNewsFeed";
import WatchlistMatrix from "@/pages/WatchlistMatrix";
import AdminDashboard from "@/pages/AdminDashboard";
import Layout from "@/components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export { API };

// localStorage key for client-side session gate
const SESSION_KEY = "wokelo_authenticated";

function App() {
  // Initialize authStep immediately from localStorage — no "checking" flash for new visitors
  const [authStep, setAuthStep] = useState(() => {
    return localStorage.getItem(SESSION_KEY) ? "checking" : "login";
  });
  const [authError, setAuthError] = useState("");

  // Only verify with backend when a local session exists (avoids shared-server token bypass)
  useEffect(() => {
    if (authStep !== "checking") return;
    const checkAuth = async () => {
      try {
        const res = await fetch(`${API}/company-news/auth`);
        const data = await res.json();
        if (data.configured) {
          if (data.username) localStorage.setItem("wokelo_username", data.username);
          setAuthStep("authenticated");
        } else {
          // Backend token gone (expired/cleared) — purge local session too
          localStorage.removeItem(SESSION_KEY);
          setAuthStep("login");
        }
      } catch {
        localStorage.removeItem(SESSION_KEY);
        setAuthStep("login");
      }
    };
    checkAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoginSuccess = async (credentials) => {
    setAuthError("");
    try {
      const res = await fetch(`${API}/company-news/auth`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: credentials.username,
          password: credentials.password,
          client_id: credentials.clientId,
          client_secret: credentials.clientSecret,
        }),
      });

      const data = await res.json();

      if (res.ok && data.status === "ok") {
        // Stamp a client-side session so only THIS browser stays logged in
        localStorage.setItem(SESSION_KEY, Date.now().toString());
        if (credentials.username) localStorage.setItem("wokelo_username", credentials.username);
        setAuthStep("authenticated");
      } else {
        setAuthError(data.detail || "Authentication failed. Please check your credentials.");
      }
    } catch (err) {
      setAuthError("Network error. Please check your connection and try again.");
    }
  };

  const handleLogout = async () => {
    // Clear client-side session first so redirect is instant
    localStorage.removeItem(SESSION_KEY);
    try {
      await fetch(`${API}/company-news/auth`, { method: "DELETE" });
    } catch {
      // Ignore network errors on logout
    }
    setAuthStep("login");
  };

  // Brief loading screen only when verifying an existing local session
  if (authStep === "checking") {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "linear-gradient(135deg, #DCDFE2 0%, #C8CDD1 100%)" }}
      >
        <div className="flex flex-col items-center gap-4">
          <img src="/wokelo-logo.png" alt="Wokelo" className="h-10 mb-2" />
          <div className="w-6 h-6 border-2 border-[#0A0A0A] border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-[#6B7280]">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        {/* Unauthenticated: /login shows the form; every other path redirects to /login */}
        {authStep === "login" && (
          <Routes>
            <Route
              path="/login"
              element={<LoginPage onLoginSuccess={handleLoginSuccess} error={authError} />}
            />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        )}

        {/* Authenticated: /login redirects away; all app routes render normally */}
        {authStep === "authenticated" && (
          <Layout onLogout={handleLogout}>
            <Routes>
              <Route path="/login" element={<Navigate to="/briefing" replace />} />
              <Route path="/" element={<Navigate to="/briefing" replace />} />
              <Route path="/briefing" element={<Dashboard />} />
              <Route path="/portfolio-matrix" element={<PortfolioMatrix />} />
              <Route path="/company-news" element={<CompanyNews />} />
              <Route path="/story/:storyId" element={<StoryDeepDive />} />
              <Route path="/settings/portfolio-data" element={<PortfolioDataSettings />} />
              <Route path="/watchlist" element={<WatchlistNewsFeed />} />
              <Route path="/watchlist-matrix" element={<WatchlistMatrix />} />
              <Route path="/flagged-companies" element={<FlaggedCompanies />} />
              <Route path="/admin" element={<AdminDashboard />} />
              {/* Legacy redirects */}
              <Route path="/dashboard" element={<Navigate to="/briefing" replace />} />
              <Route path="/news-stories" element={<Navigate to="/briefing" replace />} />
              <Route path="/stories" element={<Navigate to="/briefing" replace />} />
              <Route path="/news-feed" element={<Navigate to="/briefing" replace />} />
              <Route path="*" element={<Navigate to="/briefing" replace />} />
            </Routes>
          </Layout>
        )}
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
